import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
  IAutoallocateResult,
  IEventMessageData,
  IServerMessageStreamInput,
} from 'app/models/interfaces';
import { ApiService } from 'app/services/api.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EModuleIDs } from '../../../../consts/enums';
import { SharedService } from 'app/services/shared.service';
import { NoticeService } from 'app/services/notice.service';

@Component({
  templateUrl: './server-message-stream-dialog.component.html',
  styleUrls: ['./server-message-stream-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServerMessageStreamDialogComponent implements OnInit, OnDestroy {
  @ViewChild('terminal', { static: true }) private terminalWindow: ElementRef<HTMLDivElement>;
  @ViewChild('solutionFile') private solutionFileLink: ElementRef<HTMLAnchorElement>;
  public progressBarValue = 0;
  public totalJobs = Math.max(1, this.data.jobCount ?? 1);
  public currentJobIndex = 1;
  private currentJobResultProcessed = false;
  public messages: Array<{
    text: SafeHtml,
    class?: string,
  }> = [];
  private eventSource: EventSource;

  public allocationResult: IAutoallocateResult;
  public fileLink = '';
  cancelled = false;
  /** stream reading is finish */
  readingIsFinished = false;

  /** async backgroup process is finished */
  asyncProcessIsFinished = false;

  private readonly markForCheck = new Subject();
  private readonly subscriptions = [
    this.markForCheck.pipe(debounceTime(500)).subscribe(() => {
      this.cdr.detectChanges();
      this.scrollToTerminalBottom();
    })
  ];

  get isAsyncImport() {
    return this.data.asyncImportId;
  }

  public initializing = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IServerMessageStreamInput,
    private dialogRef: MatDialogRef<ServerMessageStreamDialogComponent, IAutoallocateResult>,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private api: ApiService,
    private sharedService: SharedService,
    private notice: NoticeService,
  ) {
    // @ts-ignore
    window.showOrder = (id: number) => window.open(this.sharedService.linkToPage(EModuleIDs.Orders, id), '_blank');
    // @ts-ignore
    window.showClient = (id: number) => window.open(this.sharedService.linkToPage(EModuleIDs.Customers, id), '_blank');
    // @ts-ignore
    window.showDriver = (id: number) => window.open(this.sharedService.linkToPage(EModuleIDs.Drivers, id), '_blank');
  }

  ngOnInit(): void {
    const eventSourceLink = this.data.eventSourceLink ?? this.api.getImportStreamLink(this.data.asyncImportId);

    if (!eventSourceLink) {
      return;
    }

    this.asyncProcessIsFinished = this.data.asyncFinished;

    this.openJob(eventSourceLink);
  }

  private openJob(eventSourceLink: string): void {
    this.currentJobResultProcessed = false;
    this.eventSource = new EventSource(eventSourceLink);
    this.eventSource.onmessage = (e: MessageEvent) => {
      const messageData: IEventMessageData = JSON.parse(e.data);
      this.addMessage(messageData);
      this.initializing = false;
      this.markForCheck.next();
    };

    this.eventSource.onerror = () => {
      this.eventSource.close();

      if (
        this.data.allocateOrderToManifest === true &&
        !this.currentJobResultProcessed
      ) {
        // onerror is our only completion signal, and it fires identically
        // for a transient connection drop and a genuinely finished job -
        // we can't tell them apart. Surface it instead of silently
        // treating this job's missing routes as a success.
        this.messages.push({
          text: this.domSanitizer.bypassSecurityTrustHtml(
            `Manifest ${this.currentJobIndex} of ${this.totalJobs}: connection closed before a result was received - this job's routes may be missing.`
          ),
          class: 'error',
        });
      }

      if (this.currentJobIndex < this.totalJobs) {
        this.currentJobIndex++;
        this.messages.push({
          text: this.domSanitizer.bypassSecurityTrustHtml(
            `--- Manifest ${this.currentJobIndex} of ${this.totalJobs} ---`
          ),
        });
        this.openJob(eventSourceLink);
      } else {
        this.progressBarValue = 100;
        this.readingIsFinished = true;
      }
      this.markForCheck.next();
    };
  }

  private addMessage(messageData: IEventMessageData) {
    if (messageData.current_idx != null && messageData.tot_qty > 0) {
      const jobProgress = messageData.current_idx / messageData.tot_qty;
      this.progressBarValue =
        ((this.currentJobIndex - 1) + jobProgress) / this.totalJobs * 100;
    }

    if (
      this.data.allocateOrderToManifest === true &&
      messageData.data &&
      !this.currentJobResultProcessed
    ) {
      const incoming: IAutoallocateResult = JSON.parse(messageData.data);
      if (incoming?.result === 1) {
        this.currentJobResultProcessed = true;
        this.allocationResult = this.mergeAutoallocateResults(
          this.allocationResult,
          incoming
        );
      }
    }

    if (messageData.file) {
      const xlsContent = (
        'data:application/octet-stream;charset=utf-16le;base64,'
        + messageData.file
      );
      this.fileLink = this.domSanitizer.sanitize(
        SecurityContext.URL,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          encodeURI(xlsContent)
        ),
      );
      const link = this.solutionFileLink.nativeElement;
      link.setAttribute('href', this.fileLink);
      link.setAttribute('download', 'solution.xlsx');
      link.text = 'solution.xlsx';
      this.solutionFileLink.nativeElement.click();
    }

    this.messages.push({
      text: this.domSanitizer.bypassSecurityTrustHtml(
        messageData.message
      ),
      class: messageData.is_error ? 'error' : '',
    });
  }

  private mergeAutoallocateResults(
    existing: IAutoallocateResult | undefined,
    incoming: IAutoallocateResult
  ): IAutoallocateResult {
    if (!existing) {
      return incoming;
    }
    return {
      // Success if any job succeeded, not only if every job did - addMessage
      // only ever calls this with an already-successful incoming result, but
      // this shouldn't rely on that guard to stay correct.
      result: existing.result || incoming.result ? 1 : 0,
      // The backend recomputes shift_starts_at per run - keep the latest
      // job's value rather than staying stuck on job 1's.
      shift_starts_at: incoming.shift_starts_at,
      // Defensive: not every allocateOrderToManifest stream's payload is
      // guaranteed to carry routes (e.g. a manifest-search result) - never
      // throw on a shape that doesn't match IAutoallocateResult.
      routes: [...(existing.routes ?? []), ...(incoming.routes ?? [])],
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());

    if (this.eventSource) {
      this.eventSource.close();
    }
  }

  private scrollToTerminalBottom = (): void => {
    this.terminalWindow.nativeElement.scrollIntoView(false);
  }

  public allocate = (): void => {
    this.dialogRef.close(this.allocationResult);
  }

  public cancelImport = () => {
    this.cancelled = true;
    this.api.cancelImport(this.data.asyncImportId).subscribe(() => {
      this.notice.showSnackBar('Cancellation process requested. You can close the pop-up window.');
    });
  }
}
